import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Drawer, Popover } from '@mui/material';
import clsx from 'clsx';
import { IS_SANDBOX_ENV } from 'helper/constant';
import PropTypes from 'prop-types';

import { updateEnvironmentWarningAction } from 'redux/actions/apiCall';
import { selectIsSandboxAdminExist } from 'redux/selectors/auth';
import { selectUserRole } from 'redux/selectors/general';

import styles from './switchEnvironment.module.css';

import { ReactComponent as ChevronDownIcon } from 'asset/IconsPack/at_chevron-down-filled.svg';

import ChangeEnvironmentWarning from '../ChangeEnvironmentWarning';
import EnvironmentList from './EnvironmentList';
import { ENV_LABEL, NEEDS_SANDBOX_ADMIN, SWITCH_TARGET } from './constants';

function SwitchEnvironment({ isMobile }) {
    const dispatch = useDispatch();
    const isSandboxAdminExist = useSelector(selectIsSandboxAdminExist);
    const userRole = useSelector(selectUserRole);
    const [anchorEl, setAnchorEl] = useState(null);

    const canSwitch = !NEEDS_SANDBOX_ADMIN || isSandboxAdminExist;
    const canRequestSandbox = !canSwitch && userRole === 'company_owner';
    const hasMenu = canSwitch || canRequestSandbox;
    const isOpen = Boolean(anchorEl);

    const closeMenu = () => {
        setAnchorEl(null);
    };

    const handleSwitch = () => {
        closeMenu();
        dispatch(updateEnvironmentWarningAction(true));
    };

    const status = (
        <>
            <span className={styles.dot} aria-hidden="true" />
            {ENV_LABEL}
        </>
    );

    const list = (
        <EnvironmentList canSwitch={canSwitch} onSwitch={handleSwitch} onClose={closeMenu} />
    );

    return (
        <>
            {hasMenu ? (
                <button
                    type="button"
                    data-tour="environment"
                    className={clsx(
                        styles.trigger,
                        IS_SANDBOX_ENV && styles.sandboxTrigger,
                        isOpen && styles.triggerOpen
                    )}
                    onClick={(event) => setAnchorEl(event.currentTarget)}
                    aria-haspopup="menu"
                    aria-expanded={isOpen}
                    aria-controls={isOpen ? 'environment-menu' : undefined}
                >
                    {status}
                    <ChevronDownIcon className={styles.chevron} aria-hidden="true" />
                </button>
            ) : (
                <span className={clsx(styles.trigger, styles.statusOnly)} data-tour="environment">
                    {status}
                </span>
            )}
            {hasMenu && isMobile && (
                <Drawer
                    anchor="bottom"
                    open={isOpen}
                    onClose={closeMenu}
                    classes={{ paper: styles.sheetPaper }}
                >
                    {list}
                </Drawer>
            )}
            {hasMenu && !isMobile && (
                <Popover
                    open={isOpen}
                    anchorEl={anchorEl}
                    onClose={closeMenu}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    classes={{ paper: styles.menuPaper }}
                >
                    {list}
                </Popover>
            )}
            <ChangeEnvironmentWarning targetEnv={SWITCH_TARGET} />
        </>
    );
}

SwitchEnvironment.propTypes = {
    isMobile: PropTypes.bool,
};

export default SwitchEnvironment;
